import React from 'react';
import { FiEdit2, FiTrash2, FiSave, FiX } from 'react-icons/fi';
import { ExamTopicNote } from '../../../../services/examNotesService';
import MarkdownRenderer from './MarkdownRenderer';

interface ExamNotesPanelProps {
  hasTopics: boolean;
  selectedTopicTitle: string;
  notes: ExamTopicNote[];
  isLoadingNotes: boolean;
  notesError: string | null;
  noteDraft: string;
  noteDraftError: string | null;
  onNoteDraftChange: (value: string) => void;
  onSaveNote: () => void;
  isSavingNote: boolean;
  editingNoteId: string | null;
  editingNoteDraft: string;
  editingNoteError: string | null;
  onStartEdit: (noteId: string, content: string) => void;
  onEditDraftChange: (value: string) => void;
  onCancelEdit: () => void;
  onUpdateNote: () => void;
  updatingNoteId: string | null;
  deletingNoteId: string | null;
  onDeleteNote: (noteId: string) => void;
  maxLength: number;
}

type NoteFormatTool = {
  key: string;
  label: string;
  title: string;
  type: 'insert' | 'wrap';
  prefix: string;
  suffix?: string;
};

const MARKDOWN_TOOLS: NoteFormatTool[] = [
  { key: 'heading', label: 'H2', title: 'Heading', type: 'insert', prefix: '## ' },
  { key: 'bold', label: 'B', title: 'Bold', type: 'wrap', prefix: '**', suffix: '**' },
  { key: 'italic', label: 'I', title: 'Italic', type: 'wrap', prefix: '*', suffix: '*' },
  { key: 'code', label: 'Code', title: 'Inline code', type: 'wrap', prefix: '`', suffix: '`' },
  { key: 'list', label: '• List', title: 'Bullet list', type: 'insert', prefix: '- ' },
  { key: 'numbered', label: '1.', title: 'Numbered list', type: 'insert', prefix: '1. ' },
  { key: 'task', label: '[ ]', title: 'Task list', type: 'insert', prefix: '- [ ] ' },
  { key: 'quote', label: 'Quote', title: 'Quote', type: 'insert', prefix: '> ' },
  { key: 'link', label: 'Link', title: 'Insert link', type: 'wrap', prefix: '[', suffix: '](https://)' },
  { key: 'math-block', label: '$$', title: 'Math block', type: 'wrap', prefix: '$$\n', suffix: '\n$$' },
];

const MATH_SYMBOLS = ['π', '√', '∑', '∫', '∞', '≈', '≠', '≤', '≥', '±', '×', '÷', '°', '→', '←'];
const SCIENCE_SYMBOLS = ['Δ', 'λ', 'μ', 'Ω', 'α', 'β', 'γ', 'θ', 'H₂O', 'CO₂', 'O₂', 'Na⁺', 'K⁺', 'pH', 'mol'];
const FORMULA_TOOLS: NoteFormatTool[] = [
  { key: 'inline-math', label: '$x$', title: 'Inline formula', type: 'wrap', prefix: '$', suffix: '$' },
  { key: 'power', label: 'x^n', title: 'Superscript', type: 'wrap', prefix: '^{', suffix: '}' },
  { key: 'subscript', label: 'x_n', title: 'Subscript', type: 'wrap', prefix: '_{', suffix: '}' },
  { key: 'fraction', label: 'a/b', title: 'Fraction', type: 'insert', prefix: '\\frac{}{}' },
  { key: 'sqrt', label: 'sqrt', title: 'Square root', type: 'insert', prefix: '\\sqrt{}' },
  { key: 'n-root', label: 'n-root', title: 'Nth root', type: 'insert', prefix: '\\sqrt[n]{}' },
  { key: 'sum', label: 'sum', title: 'Summation', type: 'insert', prefix: '\\sum_{i=1}^{n}' },
  { key: 'integral', label: 'int', title: 'Integral', type: 'insert', prefix: '\\int_{a}^{b}' },
  { key: 'limit', label: 'lim', title: 'Limit', type: 'insert', prefix: '\\lim_{x \\to }' },
  { key: 'vector', label: 'vec', title: 'Vector', type: 'insert', prefix: '\\vec{}' },
  { key: 'matrix', label: 'matrix', title: 'Matrix template', type: 'insert', prefix: '\\begin{bmatrix}\na & b \\\\\nc & d\n\\end{bmatrix}' },
  { key: 'reaction', label: 'A->B', title: 'Reaction', type: 'insert', prefix: 'A + B \\rightarrow C' },
  { key: 'equilibrium', label: 'A<->B', title: 'Equilibrium', type: 'insert', prefix: 'A + B \\rightleftharpoons C' },
];

const LATEX_SNIPPETS = ['\\frac{}{}', '\\sqrt{}', '\\times', '\\pm', '\\Delta'];

const formatNoteTime = (dateValue: string) => {
  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return 'Unknown time';
  }

  return parsedDate.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ExamNotesPanel: React.FC<ExamNotesPanelProps> = ({
  hasTopics,
  selectedTopicTitle,
  notes,
  isLoadingNotes,
  notesError,
  noteDraft,
  noteDraftError,
  onNoteDraftChange,
  onSaveNote,
  isSavingNote,
  editingNoteId,
  editingNoteDraft,
  editingNoteError,
  onStartEdit,
  onEditDraftChange,
  onCancelEdit,
  onUpdateNote,
  updatingNoteId,
  deletingNoteId,
  onDeleteNote,
  maxLength,
}) => {
  const noteTextareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const editTextareaRef = React.useRef<HTMLTextAreaElement | null>(null);

  const applyTextUpdate = React.useCallback(
    (
      textarea: HTMLTextAreaElement | null,
      currentValue: string,
      onChange: (value: string) => void,
      type: NoteFormatTool['type'],
      prefix: string,
      suffix = ''
    ) => {
      if (!textarea) {
        if (type === 'wrap') {
          onChange(`${currentValue}${prefix}${suffix}`);
        } else {
          onChange(`${currentValue}${prefix}`);
        }
        return;
      }

      const selectionStart = textarea.selectionStart ?? currentValue.length;
      const selectionEnd = textarea.selectionEnd ?? currentValue.length;
      const before = currentValue.slice(0, selectionStart);
      const selected = currentValue.slice(selectionStart, selectionEnd);
      const after = currentValue.slice(selectionEnd);

      let nextValue = currentValue;
      let nextSelectionStart = selectionStart;
      let nextSelectionEnd = selectionEnd;

      if (type === 'wrap') {
        nextValue = `${before}${prefix}${selected}${suffix}${after}`;
        if (selected.length === 0) {
          nextSelectionStart = before.length + prefix.length;
          nextSelectionEnd = nextSelectionStart;
        } else {
          nextSelectionStart = before.length + prefix.length;
          nextSelectionEnd = nextSelectionStart + selected.length;
        }
      } else {
        nextValue = `${before}${prefix}${after}`;
        nextSelectionStart = before.length + prefix.length;
        nextSelectionEnd = nextSelectionStart;
      }

      onChange(nextValue);
      window.requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(nextSelectionStart, nextSelectionEnd);
      });
    },
    []
  );

  const renderFormattingToolbar = (
    currentValue: string,
    onChange: (value: string) => void,
    textareaRef: React.RefObject<HTMLTextAreaElement | null>,
  ) => (
    <div className="exam-note-toolbar" role="group" aria-label="Note formatting tools">
      <div className="exam-note-toolbar-group">
        <span className="exam-note-toolbar-label">Markdown</span>
        {MARKDOWN_TOOLS.map((tool) => (
          <button
            key={tool.key}
            type="button"
            className="exam-note-tool-btn"
            title={tool.title}
            onClick={() =>
              applyTextUpdate(textareaRef.current, currentValue, onChange, tool.type, tool.prefix, tool.suffix)
            }
          >
            {tool.label}
          </button>
        ))}
      </div>

      <div className="exam-note-toolbar-group">
        <span className="exam-note-toolbar-label">Formula</span>
        {FORMULA_TOOLS.map((tool) => (
          <button
            key={tool.key}
            type="button"
            className="exam-note-tool-btn"
            title={tool.title}
            onClick={() =>
              applyTextUpdate(textareaRef.current, currentValue, onChange, tool.type, tool.prefix, tool.suffix)
            }
          >
            {tool.label}
          </button>
        ))}
      </div>

      <div className="exam-note-toolbar-group">
        <span className="exam-note-toolbar-label">Math</span>
        {MATH_SYMBOLS.map((symbol) => (
          <button
            key={symbol}
            type="button"
            className="exam-note-tool-btn symbol"
            onClick={() => applyTextUpdate(textareaRef.current, currentValue, onChange, 'insert', symbol)}
          >
            {symbol}
          </button>
        ))}
      </div>

      <div className="exam-note-toolbar-group">
        <span className="exam-note-toolbar-label">Science</span>
        {SCIENCE_SYMBOLS.map((symbol) => (
          <button
            key={symbol}
            type="button"
            className="exam-note-tool-btn symbol"
            onClick={() => applyTextUpdate(textareaRef.current, currentValue, onChange, 'insert', symbol)}
          >
            {symbol}
          </button>
        ))}
      </div>

      <div className="exam-note-toolbar-group">
        <span className="exam-note-toolbar-label">LaTeX</span>
        {LATEX_SNIPPETS.map((snippet) => (
          <button
            key={snippet}
            type="button"
            className="exam-note-tool-btn"
            onClick={() => applyTextUpdate(textareaRef.current, currentValue, onChange, 'insert', snippet)}
          >
            {snippet}
          </button>
        ))}
      </div>

      <p className="exam-note-toolbar-hint">
        Tip: use <code>$...$</code> for inline formulas and <code>$$...$$</code> for multi-line formulas.
      </p>
    </div>
  );

  if (!hasTopics) {
    return (
      <section className="exam-notes-panel">
        <div className="exam-notes-empty">
          <h2>No topics available for this exam.</h2>
          <p>Create or open an exam plan to start writing notes by topic.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="exam-notes-panel">
      <div className="exam-notes-header">
        <h2>Notes: {selectedTopicTitle}</h2>
        <span className="exam-notes-count">{notes.length} note{notes.length === 1 ? '' : 's'}</span>
      </div>

      <div className="exam-note-composer">
        <label htmlFor="exam-note-input">Add note</label>
        {renderFormattingToolbar(noteDraft, onNoteDraftChange, noteTextareaRef)}
        <textarea
          id="exam-note-input"
          ref={noteTextareaRef}
          value={noteDraft}
          onChange={(event) => onNoteDraftChange(event.target.value)}
          placeholder="Write your note for this topic..."
          rows={4}
        />
        <div className="exam-note-composer-meta">
          <span className={noteDraft.trim().length > maxLength ? 'over-limit' : ''}>
            {noteDraft.trim().length}/{maxLength}
          </span>
          <button
            type="button"
            className="exam-note-save-btn"
            onClick={onSaveNote}
            disabled={isSavingNote || noteDraft.trim().length === 0}
          >
            <FiSave size={14} />
            {isSavingNote ? 'Saving...' : 'Save note'}
          </button>
        </div>
        {noteDraftError && <p className="exam-notes-error">{noteDraftError}</p>}
      </div>

      {notesError && <p className="exam-notes-error">{notesError}</p>}

      {isLoadingNotes ? (
        <div className="exam-notes-loading">Loading notes...</div>
      ) : notes.length === 0 ? (
        <div className="exam-notes-empty">
          <h3>No notes yet for this topic.</h3>
          <p>Add your first note above.</p>
        </div>
      ) : (
        <div className="exam-notes-list">
          {notes.map((note) => {
            const isEditing = editingNoteId === note.noteId;
            const isUpdating = updatingNoteId === note.noteId;
            const isDeleting = deletingNoteId === note.noteId;

            return (
              <article key={note.noteId} className="exam-note-item">
                <div className="exam-note-item-meta">
                  <span>{formatNoteTime(note.createdAt)}</span>
                </div>

                {isEditing ? (
                  <div className="exam-note-edit-area">
                    {renderFormattingToolbar(editingNoteDraft, onEditDraftChange, editTextareaRef)}
                    <textarea
                      ref={editTextareaRef}
                      value={editingNoteDraft}
                      onChange={(event) => onEditDraftChange(event.target.value)}
                      rows={4}
                    />
                    <div className="exam-note-item-actions">
                      <button
                        type="button"
                        className="exam-note-action-btn"
                        onClick={onUpdateNote}
                        disabled={isUpdating}
                      >
                        <FiSave size={14} />
                        {isUpdating ? 'Updating...' : 'Update'}
                      </button>
                      <button type="button" className="exam-note-action-btn ghost" onClick={onCancelEdit}>
                        <FiX size={14} />
                        Cancel
                      </button>
                    </div>
                    {editingNoteError && <p className="exam-notes-error">{editingNoteError}</p>}
                  </div>
                ) : (
                  <>
                    <div className="exam-note-content">
                      <MarkdownRenderer content={note.content} />
                    </div>
                    <div className="exam-note-item-actions">
                      <button
                        type="button"
                        className="exam-note-action-btn ghost"
                        onClick={() => onStartEdit(note.noteId, note.content)}
                      >
                        <FiEdit2 size={14} />
                        Edit
                      </button>
                      <button
                        type="button"
                        className="exam-note-action-btn danger"
                        onClick={() => onDeleteNote(note.noteId)}
                        disabled={isDeleting}
                      >
                        <FiTrash2 size={14} />
                        {isDeleting ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default ExamNotesPanel;
